import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import toast from 'Utils/toast';
import { getMfaStatus, disableMfa } from '../../services/mfaService';
import UserContext from '../../Shared/UserContext';
import GlobalLoader from '../../Shared/GlobalLoader/GlobalLoader';

/**
 * MfaManagement - Component for managing MFA settings after login
 * Allows users to view status, disable MFA, generate backup codes, etc.
 */
const MfaManagement = () => {
  const { state } = useContext(UserContext);
  const { token } = state;

  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const loadMfaStatus = async () => {
    try {
      setLoading(true);
      const status = await getMfaStatus(token);
      setMfaStatus(status);
    } catch (error) {
      console.error('Error loading MFA status:', error);
      toast.error('Erreur lors du chargement du statut de l\'Authentification Multi-Facteurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const handleDisableConfirm = async () => {
    try {
      setDisabling(true);
      await disableMfa(token);
      toast.success('Authentification à deux facteurs désactivée avec succès');
      setDisableDialogOpen(false);
      // Reload status
      await loadMfaStatus();
    } catch (error) {
      console.error('Error disabling MFA:', error);
      toast.error('Erreur lors de la désactivation de l\'Authentification Multi-Facteurs');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return <GlobalLoader size="50vh" />;
  }

  if (!mfaStatus) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            Impossible de charger les paramètres d&apos;authentification
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, margin: '0 auto', padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        Authentification à deux facteurs
      </Typography>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Statut actuel
          </Typography>

          {mfaStatus.enabled ? (
            <Alert severity="success" sx={{ marginBottom: 2 }}>
              L&apos;authentification à deux facteurs est activée et protège votre compte
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ marginBottom: 2 }}>
              L&apos;authentification à deux facteurs n&apos;est pas activée
            </Alert>
          )}

          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Statut:</strong>
              {' '}
              {mfaStatus.enabled ? 'Activée' : 'Désactivée'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Requise:</strong>
              {' '}
              {mfaStatus.required ? 'Oui' : 'Non'}
            </Typography>
            {mfaStatus.enabled && (
              <Typography variant="body2" color="textSecondary">
                <strong>Codes de sauvegarde:</strong>
                {' '}
                {mfaStatus.hasBackupCodes
                  ? `${mfaStatus.remainingBackupCodes} restants`
                  : 'Aucun'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {mfaStatus.enabled && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Actions disponibles
            </Typography>

            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Si vous avez perdu l&apos;accès à votre application d&apos;authentification ou
              si vous souhaitez reconfigurer votre authentification à deux facteurs,
              vous pouvez la réinitialiser.
            </Typography>

            <Alert severity="warning" sx={{ marginBottom: 2 }}>
              <strong>Attention:</strong>
              {' '}
              La réinitialisation supprimera votre configuration actuelle.
              Vous devrez reconfigurer votre authentification lors de votre prochaine connexion.
            </Alert>

            <Button
              variant="outlined"
              color="warning"
              onClick={() => setDisableDialogOpen(true)}
              disabled={disabling}
            >
              Réinitialiser l&apos;authentification
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Disable Confirmation Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)}>
        <DialogTitle>
          Réinitialiser l&apos;authentification à deux facteurs
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir réinitialiser votre authentification à deux facteurs ?
            <br />
            <br />
            Cette action va :
            <br />
            • Supprimer votre configuration actuelle
            <br />
            • Supprimer tous vos codes de sauvegarde
            <br />
            • Vous demander de reconfigurer l&apos;authentification lors de votre prochaine
            {' '}
            connexion
            <br />
            <br />
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisableDialogOpen(false)}
            disabled={disabling}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDisableConfirm}
            color="warning"
            variant="contained"
            disabled={disabling}
          >
            {disabling ? 'Réinitialisation...' : 'Réinitialiser'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MfaManagement;
